<!-- Single image thumbnail in a chat attachment grid, with fallback loading and "+N more" overflow tile. -->

<script lang="ts" setup>
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { computed, ref, watch } from 'vue';

import * as l from './ChatMessageImageTile.loc.js';

interface Props {
  fileName?: string | null;
  url?: string | null;
  previewUrl?: string | null;
  /** When set and > 0, renders an overlay showing "+{overflowCount} more" over the image. */
  overflowCount?: number | null;
  /** When true, the tile preserves the image's natural aspect ratio (single-image layout). */
  preserveAspect?: boolean;
  /** Image natural width — used together with preserveAspect. */
  width?: number | null;
  /** Image natural height — used together with preserveAspect. */
  height?: number | null;
  /**
   * Bubble color — controls the placeholder background visible while the image
   * is pending or fails to load. Blue bubbles use a white placeholder so the
   * image area doesn't blend into the bubble.
   */
  color?: 'blue' | 'white';
}

interface Emits {
  /** Emitted when the tile is activated as a button (overflow mode). */
  (e: 'overflowClick'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const t = useTranslate();

const previewLoadFailed = ref(false);

const isOverflow = computed(() => props.overflowCount != null && props.overflowCount > 0);

const overflowAriaLabel = computed(() => {
  if (!isOverflow.value) {
    return undefined;
  }
  return t(l.showMoreImages, { count: props.overflowCount ?? 0 });
});

const imageSrc = computed(() => {
  if (previewLoadFailed.value || !props.previewUrl) {
    return props.url ?? undefined;
  }
  return props.previewUrl;
});

function handleOverflowClick() {
  emit('overflowClick');
}

const aspectRatio = computed(() => {
  if (!props.preserveAspect) {
    return undefined;
  }
  if (props.width != null && props.height != null && props.height !== 0) {
    return (props.width / props.height).toString();
  }
  return '4 / 3';
});

watch([() => props.previewUrl, () => props.url], () => {
  previewLoadFailed.value = false;
});

function handleImageError() {
  if (!previewLoadFailed.value && props.previewUrl && props.url) {
    previewLoadFailed.value = true;
  }
}
</script>

<template>
  <button
    v-if="isOverflow"
    tid="file-preview"
    type="button"
    :aria-label="overflowAriaLabel"
    :class="[css.tile, css.tile_button, color && css[`color_${color}`], preserveAspect && css.tile_preserveAspect]"
    :style="aspectRatio ? { aspectRatio } : undefined"
    @click="handleOverflowClick"
  >
    <img
      v-if="imageSrc"
      :src="imageSrc"
      :alt="fileName ?? undefined"
      loading="lazy"
      :class="css.image"
      @error="handleImageError"
    />

    <div :class="css.overflow">
      <span :class="css.overflowLabel">{{ t(l.moreOverflow, { count: overflowCount ?? 0 }) }}</span>
    </div>
  </button>

  <a
    v-else
    tid="file-preview"
    :href="url ?? undefined"
    target="_blank"
    rel="noopener noreferrer"
    :aria-label="fileName ?? t(l.imageAttachment)"
    :class="[css.tile, color && css[`color_${color}`], preserveAspect && css.tile_preserveAspect]"
    :style="aspectRatio ? { aspectRatio } : undefined"
  >
    <img
      v-if="imageSrc"
      :src="imageSrc"
      :alt="fileName ?? undefined"
      loading="lazy"
      :class="css.image"
      @error="handleImageError"
    />
  </a>
</template>

<style lang="scss" module="css">
.tile {
  position: relative;
  display: block;
  width: 100%;
  border-radius: $border-radius-08;
  overflow: hidden;
  // Placeholder background visible while loading or when the image errors out.
  // Defaults to the doctor-100 tint (used inside white bubbles and standalone).
  background: var(--image-tile-bg, #{$color-doctor-100});
  cursor: pointer;

  &:not(.tile_preserveAspect) {
    aspect-ratio: 1;
  }

  &_button {
    padding: 0;
    border: none;
    font: inherit;
    color: inherit;
    text-align: inherit;
    appearance: none;
  }
}

// In blue (outgoing) bubbles the bubble itself is already tinted, so use a
// white placeholder for pending/errored images — otherwise the tile blends
// into the bubble and looks like empty space.
.color_blue {
  --image-tile-bg: #{$color-white};
}

.image {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.overflow {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba($color-doctor-700, 0.7);
  transition: background-color 150ms ease-out;

  .tile:hover & {
    background: rgba($color-doctor-700, 0.8);
  }
}

.overflowLabel {
  font-size: $font-size-18;
  font-weight: 600;
  color: $color-white;
  line-height: 1;
}
</style>
